import { useState } from "react";
import { cn } from "./cn";
import {
  DersHedefFarki,
  farkYonu,
  isaretliFark,
  netMetni,
  type DersHedefSatiri,
} from "./DersHedefFarki";
import { CizgiGrafik } from "./grafik/CizgiGrafik";
import type { GrafikNoktasi } from "./grafik/tipler";

/**
 * DENEME NET GELİŞİMİ — dalga 3 (F-EXM-09, F-PAR-03)
 *
 * ================================================================
 * SINAV TÜRLERİ TEK ÇİZGİDE BİRLEŞMEZ
 * ================================================================
 * TYT 120, AYT 160, LGS 90 soruluk ölçeklerde; aynı çizgide "TYT 85 → AYT
 * 40" düşüş gibi okunurdu. Her tür kendi çizgisi; birden çok tür varsa üstte
 * tür seçici, varsayılan EN SON denemenin türü.
 *
 * ================================================================
 * HEDEF ÇİZGİSİ — F-EXM-09 (Emre kararı 07.10.2026)
 * ================================================================
 * Ders bazlı hedefi KOÇ girer; toplam hedef derslerin toplamıdır ve SUNUCUDAN gelir
 * (`hedef` = satırın toplam hedefi, `hedefFark` = sunucunun `toplamFark`ı). Ekran
 * toplamı ya da farkı KENDİSİ hesaplamaz: ikinci bir hesap, sunucuyla ayrışıp
 * öğrenciye başka bir sayı gösterirdi. Hedef gelmeyen çağıranlar (`hedef` yok)
 * eskisi gibi çalışır ve "Hedef net girilmemiş" notu kalır. Hedef puan net
 * değildir; aynı çizgiye konmaz.
 *
 * Panel, öğrenci ve veli aynı bileşeni kullanıyor; veriyi her uygulama kendi
 * yetkisiyle çekiyor (`/denemeler/ogrenci/:id/trend`, koç ASSIGNED, öğrenci
 * SELF, veli LINKED — portal bağıyla süzülmüş).
 */
export interface DenemeNetNoktasi {
  /** ISO an (`Z`/ofsetli) ya da `YYYY-AA-GG`. */
  tarih: string;
  net: number | null;
  tur: string;
  ad?: string;
  /** F-EXM-09 — o satırın sınav türünün TOPLAM hedefi (sunucu). Yok/`null`: hedef girilmemiş. */
  hedef?: number | null;
  /** Sunucunun `toplamFark`ı (`net − hedef`); verilmezse özet fark yazmaz, ekran hesaplamaz. */
  hedefFark?: number | null;
  /** Bu denemenin ders bazlı hedef farkı; son deneme için tablo olarak çizilir. */
  dersFarklari?: readonly DersHedefSatiri[] | null;
}

/** Trend satırı: `DenemeHedefFarki` ile yapısal olarak uyumlu (ui, contracts'a bağımlı değil). */
export interface HedefliDenemeSatiri {
  tarih: string;
  net: number | null;
  tur: string;
  ad?: string;
  hedefNet?: {
    toplamHedef: number;
    toplamFark: number;
    dersler: readonly DersHedefSatiri[];
  } | null;
}

/** Trend satırlarını (sunucudan `hedefNet?` ile gelen) grafik noktalarına çevirir. */
export function denemeNoktalari(satirlar: readonly HedefliDenemeSatiri[]): DenemeNetNoktasi[] {
  return satirlar.map((d) => ({
    tarih: d.tarih,
    net: d.net,
    tur: d.tur,
    ad: d.ad,
    hedef: d.hedefNet?.toplamHedef ?? null,
    hedefFark: d.hedefNet?.toplamFark ?? null,
    dersFarklari: d.hedefNet?.dersler ?? null,
  }));
}

const TUR_ADI: Readonly<Record<string, string>> = {
  TYT: "TYT",
  AYT: "AYT",
  YDT: "YDT",
  LGS: "LGS",
  BRANCH: "Branş",
  KPSS: "KPSS",
  DGS: "DGS",
  OTHER: "Diğer",
};

export function denemeTurAdi(tur: string): string {
  return TUR_ADI[tur] ?? tur;
}

/** Saf: türlere göre gruplar (eskiden yeniye), türler en son denemenin türü önde. */
export function turlereGore(denemeler: readonly DenemeNetNoktasi[]): {
  turler: string[];
  gruplar: Map<string, DenemeNetNoktasi[]>;
} {
  const sirali = [...denemeler].sort((a, b) => Date.parse(a.tarih) - Date.parse(b.tarih));
  const gruplar = new Map<string, DenemeNetNoktasi[]>();
  for (const d of sirali) gruplar.set(d.tur, [...(gruplar.get(d.tur) ?? []), d]);
  const sonTur = sirali.at(-1)?.tur;
  const turler = [...gruplar.keys()].sort((a, b) => (a === sonTur ? -1 : b === sonTur ? 1 : 0));
  return { turler, gruplar };
}

export function DenemeNetGrafigi({
  denemeler,
  baslik = "Deneme net gelişimi",
}: {
  denemeler: readonly DenemeNetNoktasi[];
  baslik?: string;
}) {
  const { turler, gruplar } = turlereGore(denemeler);
  const [secilen, setSecilen] = useState<string | null>(null);
  const tur = secilen && gruplar.has(secilen) ? secilen : (turler[0] ?? null);
  if (tur === null) return null;
  const seri = gruplar.get(tur) ?? [];
  const hedefli = seri.some((d) => typeof d.hedef === "number" && Number.isFinite(d.hedef));
  const noktalar: GrafikNoktasi[] = seri.map((d) => ({
    tarih: d.tarih,
    degerler: { net: d.net },
    // Açık `null`: "o denemede hedef yok" — ortak hedefle doldurulmaz.
    hedef: hedefli ? (d.hedef ?? null) : undefined,
  }));
  const olculen = seri.filter((d) => typeof d.net === "number");
  const ilk = olculen[0]?.net ?? null;
  const son = olculen.at(-1)?.net ?? null;
  const fark = ilk !== null && son !== null && olculen.length > 1 ? son - ilk : null;
  const sayi = (n: number) =>
    new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
  // Hedef cümlesi: son ÖLÇÜLEN deneme hedefliyse onun hedefi ve sunucu farkı; değilse en son hedef.
  const sonOlculen = olculen.at(-1);
  const hedefNoktasi =
    sonOlculen && typeof sonOlculen.hedef === "number"
      ? sonOlculen
      : [...seri].reverse().find((d) => typeof d.hedef === "number");
  const hedefCumlesi =
    hedefNoktasi && typeof hedefNoktasi.hedef === "number"
      ? ` Hedef: ${netMetni(hedefNoktasi.hedef)} net` +
        (hedefNoktasi === sonOlculen && typeof hedefNoktasi.hedefFark === "number"
          ? `; son denemede ${isaretliFark(hedefNoktasi.hedefFark)} fark (${
              { ust: "hedefin üstünde", alt: "hedefin altında", hedefte: "hedefte" }[
                farkYonu(hedefNoktasi.hedefFark)
              ]
            }).`
          : ".")
      : "";
  const ozet =
    (olculen.length === 0
      ? `${denemeTurAdi(tur)} denemelerinde net ölçülmedi.`
      : `${denemeTurAdi(tur)}: ${olculen.length} denemede son net ${sayi(son!)}` +
        (fark === null
          ? "."
          : `; ilk denemeye göre ${fark > 0 ? "+" : fark < 0 ? "−" : ""}${sayi(Math.abs(fark))}.`)) +
    hedefCumlesi;
  // Ders tablosu: seçili türün SON denemesi (eski bir denemenin tablosu "son" diye gösterilmez).
  const sonSatir = seri.at(-1);
  const dersSatiri = (sonSatir?.dersFarklari?.length ?? 0) > 0 ? sonSatir : undefined;

  return (
    <section aria-label={baslik} className="flex flex-col gap-2">
      {turler.length > 1 && (
        <div role="group" aria-label="Sınav türü" className="flex flex-wrap gap-1.5">
          {turler.map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={t === tur}
              onClick={() => setSecilen(t)}
              className={cn(
                "rounded-rozet min-h-dokunma px-3 text-[13px] font-medium ring-1 transition-colors ring-inset",
                t === tur
                  ? "bg-marka-600 text-notr-0 ring-marka-600"
                  : "bg-notr-0 text-notr-700 ring-notr-200 hover:bg-notr-50",
              )}
            >
              {denemeTurAdi(t)}
            </button>
          ))}
        </div>
      )}
      <CizgiGrafik
        baslik={`${baslik} — ${denemeTurAdi(tur)}`}
        birim="net"
        ozet={ozet}
        noktalar={noktalar}
        seriler={[{ anahtar: "net", etiket: "Toplam net", ton: "marka" }]}
      />
      {!hedefli && (
        <p className="text-notr-600 text-xs">Hedef net girilmemiş; çizgide hedef gösterilmiyor.</p>
      )}
      {dersSatiri?.dersFarklari && (
        <DersHedefFarki
          dersler={dersSatiri.dersFarklari}
          aciklama={dersSatiri.ad ? `Son deneme: ${dersSatiri.ad}` : "Son deneme"}
        />
      )}
    </section>
  );
}
